// Google Analytics (GA4) wrapper. This module is the ONLY place that talks to
// window.gtag; everything else (page views, feature events, user properties,
// the consent banner, the debug overlay) goes through the helpers here. Every
// call is mirrored into an in-memory log with a pub/sub API so the debug
// overlay can show GA traffic in real time. All entry points no-op safely when
// window or gtag is missing, so Node tests can import this file directly.
export const GOOGLE_ANALYTICS_ID = "G-NZDEZLXKNR";

// Shared with the inline bootstrap in index.html <head>, which reads this key
// before the first gtag config call. Keep the key and values in sync there.
export const ANALYTICS_CONSENT_STORAGE_KEY = "cc-consent";

const OVERLAY_ENABLED_STORAGE_KEY = "alaryiq-analytics-overlay";
const SUPPRESS_STORAGE_KEY = "alaryiq-analytics-suppress";
const OVERLAY_FLAG_EVENT = "alaryiq:analytics-overlay-flag";
const ONCE_PER_SESSION_PREFIX = "alaryiq-analytics-once:";
const ANALYTICS_LOG_LIMIT = 200;

export type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

export type AnalyticsLogEntry = {
  kind: "event" | "page_view" | "user_property";
  label: string;
  params: Record<string, unknown>;
  ts: number;
};

type GtagFn = (...args: unknown[]) => void;

let lastTrackedAnalyticsPath = "";
let analyticsLog: AnalyticsLogEntry[] = [];
const logSubscribers = new Set<(log: AnalyticsLogEntry[]) => void>();

const PUBLIC_HOME_PATH = "/";

function browserWindow(): (Window & { gtag?: GtagFn }) | undefined {
  return globalThis.window as (Window & { gtag?: GtagFn }) | undefined;
}

// Test-user suppression: sessions belonging to is_test_user accounts capture
// events locally (the debug overlay keeps working) but never send them to GA.
// Sticky in localStorage so suppression holds from the first hit of the NEXT
// page load too, not just after /api/me/approval-state answers.
let sendSuppressedCache: boolean | null = null;

export function analyticsSendSuppressed(): boolean {
  if (sendSuppressedCache === null) {
    try {
      sendSuppressedCache = globalThis.localStorage?.getItem(SUPPRESS_STORAGE_KEY) === "1";
    } catch {
      sendSuppressedCache = false;
    }
  }
  return sendSuppressedCache;
}

export function setAnalyticsSendSuppressed(suppressed: boolean) {
  sendSuppressedCache = suppressed;
  try {
    if (suppressed) globalThis.localStorage?.setItem(SUPPRESS_STORAGE_KEY, "1");
    else globalThis.localStorage?.removeItem(SUPPRESS_STORAGE_KEY);
  } catch {
    /* storage unavailable; the in-memory cache still applies this session */
  }
}

function gtagFn(): GtagFn | null {
  if (analyticsSendSuppressed()) return null;
  const win = browserWindow();
  return win && typeof win.gtag === "function" ? win.gtag : null;
}

function publish(entry: AnalyticsLogEntry) {
  analyticsLog.push(entry);
  if (analyticsLog.length > ANALYTICS_LOG_LIMIT) {
    analyticsLog = analyticsLog.slice(-ANALYTICS_LOG_LIMIT);
  }
  logSubscribers.forEach((listener) => {
    try {
      listener(analyticsLog);
    } catch {
      /* one bad subscriber must not break the rest */
    }
  });
}

export function getAnalyticsLog(): AnalyticsLogEntry[] {
  return analyticsLog.slice();
}

export function subscribeAnalytics(listener: (log: AnalyticsLogEntry[]) => void): () => void {
  logSubscribers.add(listener);
  return () => logSubscribers.delete(listener);
}

export function clearAnalyticsLog() {
  analyticsLog = [];
  logSubscribers.forEach((listener) => {
    try {
      listener(analyticsLog);
    } catch {
      /* ignore */
    }
  });
}

export function resetAnalyticsTrackingForTests() {
  lastTrackedAnalyticsPath = "";
  analyticsLog = [];
  logSubscribers.clear();
  sendSuppressedCache = null;
}

function cleanParams(params: AnalyticsParams = {}): Record<string, string | number | boolean> {
  const cleaned: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    cleaned[key] = value;
  });
  return cleaned;
}

// Fires a GA custom event. Returns true when the event reached gtag.
export function track(event: string, params: AnalyticsParams = {}): boolean {
  const cleaned = cleanParams(params);
  publish({ kind: "event", label: event, params: cleaned, ts: Date.now() });
  const gtag = gtagFn();
  if (!gtag) return false;
  gtag("event", event, cleaned);
  return true;
}

// Sticky per-session user property (e.g. is_admin).
export function setUserProperty(key: string, value: string | number | boolean): boolean {
  publish({ kind: "user_property", label: key, params: { value }, ts: Date.now() });
  const gtag = gtagFn();
  if (!gtag) return false;
  gtag("set", "user_properties", { [key]: value });
  return true;
}

// Runs fn at most once per browser session (sessionStorage-backed). Fails open:
// if storage is unavailable the fn still runs.
export function fireOncePerSession(key: string, fn: () => void) {
  const storageKey = ONCE_PER_SESSION_PREFIX + key;
  try {
    const storage = globalThis.sessionStorage;
    if (storage.getItem(storageKey) === "1") return;
    storage.setItem(storageKey, "1");
  } catch {
    /* storage unavailable; fire anyway */
  }
  fn();
}

export function normalizedAnalyticsPath(path: unknown): string {
  const rawPath = String(path ?? "").trim();
  if (!rawPath || rawPath === "home" || rawPath === "/home") return PUBLIC_HOME_PATH;
  return rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
}

export function analyticsPathForAppState({ view, aircraftPanel, reportPanel }: {
  view?: string;
  aircraftPanel?: string;
  reportPanel?: string;
} = {}): string {
  if (view === "dashboard") {
    return `/aircraft/dashboard/${reportPanel || "overview"}`;
  }
  if (view === "aircraft") {
    if (aircraftPanel === "dashboard") return `/aircraft/dashboard/${reportPanel || "overview"}`;
    return `/aircraft/${aircraftPanel || "profile"}`;
  }
  return normalizedAnalyticsPath(view || "home");
}

export function trackAnalyticsPageView(path: unknown, options: { title?: string } = {}): boolean {
  const win = browserWindow();
  if (!win) return false;

  const normalizedPath = normalizedAnalyticsPath(path);
  if (normalizedPath === lastTrackedAnalyticsPath) return false;
  lastTrackedAnalyticsPath = normalizedPath;

  const title = options.title
    ?? globalThis.document?.title
    ?? "AlaryIQ";
  const origin = win.location?.origin || "https://www.alaryiq.com";
  const pageLocation = new URL(normalizedPath, `${origin}/`).href;

  const params = {
    page_title: title,
    page_path: normalizedPath,
    page_location: pageLocation,
  };
  // Capture locally even when the send is suppressed (test users) so the
  // debug overlay keeps mirroring the session.
  publish({ kind: "page_view", label: normalizedPath, params, ts: Date.now() });
  gtagFn()?.("event", "page_view", params);

  // Mark the session as an admin session the first time an admin page is seen.
  if (normalizedPath === "/admin" || normalizedPath.startsWith("/admin/")) {
    fireOncePerSession("is-admin-user-property", () => setUserProperty("is_admin", true));
  }
  return true;
}

// --- Cookie consent -------------------------------------------------------

export function storedAnalyticsConsent(): "granted" | "denied" | null {
  try {
    const value = globalThis.localStorage?.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function applyAnalyticsConsentChoice(granted: boolean) {
  try {
    globalThis.localStorage?.setItem(ANALYTICS_CONSENT_STORAGE_KEY, granted ? "granted" : "denied");
  } catch {
    /* storage unavailable; the choice just won't persist */
  }
  const gtag = gtagFn();
  if (gtag) {
    gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
  }
  track("consent_banner_choice", { choice: granted ? "accept" : "decline" });
}

// --- Debug overlay enable flag --------------------------------------------

export function analyticsOverlayEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(OVERLAY_ENABLED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setAnalyticsOverlayEnabled(enabled: boolean) {
  try {
    globalThis.localStorage?.setItem(OVERLAY_ENABLED_STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    /* storage unavailable; subscribers still get the event below */
  }
  const win = browserWindow();
  win?.dispatchEvent?.(new CustomEvent(OVERLAY_FLAG_EVENT, { detail: { enabled } }));
}

export function subscribeAnalyticsOverlayEnabled(listener: (enabled: boolean) => void): () => void {
  const win = browserWindow();
  if (!win?.addEventListener) return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
    listener(Boolean(detail?.enabled));
  };
  win.addEventListener(OVERLAY_FLAG_EVENT, handler);
  return () => win.removeEventListener(OVERLAY_FLAG_EVENT, handler);
}
